import {
  inferCurrentLocation,
  primaryOrderLabel,
  type OpsLookupSaleRow,
  type OpsLookupServiceRow,
  type OpsLookupUnitRow,
} from "@/lib/opsLookup/opsLookupModel";

export function OpsLookupSaleList(props: {
  rows: OpsLookupSaleRow[];
  unitsByDealId: Map<string, OpsLookupUnitRow>;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (!props.rows.length) {
    return <p className="text-sm text-gray-500 dark:text-zinc-400">No sales match this search.</p>;
  }
  return (
    <ul
      data-app-panel="OpsLookupSaleList"
      data-testid="ops-lookup-sale-list"
      className="divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-zinc-700 dark:border-zinc-700"
    >
      {props.rows.map((row) => {
        const unit = props.unitsByDealId.get(row.id);
        const active = props.selectedId === row.id;
        return (
          <li key={row.id}>
            <button
              type="button"
              data-testid={`ops-lookup-sale-${row.id}`}
              className={`flex w-full flex-col gap-0.5 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-zinc-800/60 ${
                active ? "bg-slate-50 dark:bg-zinc-800" : ""
              }`}
              onClick={() => props.onSelect(row.id)}
            >
              <span className="font-medium text-gray-900 dark:text-zinc-100">
                {row.display_name?.trim() || "Untitled sale"}
              </span>
              <span className="text-xs text-gray-500 dark:text-zinc-400">
                {row.status}
                {unit ? ` · ${primaryOrderLabel(unit)}` : " · no linked order"}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export function OpsLookupOrderList(props: {
  rows: OpsLookupUnitRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (!props.rows.length) {
    return <p className="text-sm text-gray-500 dark:text-zinc-400">No orders match this search.</p>;
  }
  return (
    <ul
      data-app-panel="OpsLookupOrderList"
      data-testid="ops-lookup-order-list"
      className="divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-zinc-700 dark:border-zinc-700"
    >
      {props.rows.map((row) => {
        const active = props.selectedId === row.id;
        const loc = inferCurrentLocation(row);
        return (
          <li key={row.id}>
            <button
              type="button"
              data-testid={`ops-lookup-order-${row.id}`}
              className={`flex w-full flex-col gap-0.5 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-zinc-800/60 ${
                active ? "bg-slate-50 dark:bg-zinc-800" : ""
              }`}
              onClick={() => props.onSelect(row.id)}
            >
              <span className="font-medium text-gray-900 dark:text-zinc-100">
                {primaryOrderLabel(row)}
              </span>
              <span className="text-xs text-gray-500 dark:text-zinc-400">
                {row.sale_path} · current: {loc}
                {row.ops_deal_id ? "" : " · unlinked"}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export function OpsLookupServiceList(props: {
  rows: OpsLookupServiceRow[];
  unitsById: Map<string, OpsLookupUnitRow>;
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (!props.rows.length) {
    return (
      <p className="text-sm text-gray-500 dark:text-zinc-400">No service jobs match this search.</p>
    );
  }
  return (
    <ul
      data-app-panel="OpsLookupServiceList"
      data-testid="ops-lookup-service-list"
      className="divide-y divide-gray-200 rounded-lg border border-gray-200 dark:divide-zinc-700 dark:border-zinc-700"
    >
      {props.rows.map((row) => {
        const active = props.selectedId === row.id;
        const unit = row.mfg_unit_id ? props.unitsById.get(row.mfg_unit_id) : null;
        return (
          <li key={`${row.kind}:${row.id}`}>
            <button
              type="button"
              data-testid={`ops-lookup-service-${row.id}`}
              className={`flex w-full flex-col gap-0.5 px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-zinc-800/60 ${
                active ? "bg-slate-50 dark:bg-zinc-800" : ""
              }`}
              onClick={() => props.onSelect(row.id)}
            >
              <span className="font-medium text-gray-900 dark:text-zinc-100">{row.title}</span>
              <span className="text-xs text-gray-500 dark:text-zinc-400">
                {row.kind} · {row.status}
                {unit ? ` · ${primaryOrderLabel(unit)}` : ""}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}
